import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import env from './utils/env';
import './ReportSourcesView.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import Loader from './components/Loader';

// Interface for ReportSourceCandidate (duplicated from list view for clarity)
interface ReportSourceCandidate {
    _id: string;
    url: string;
    is_pdf?: boolean;
    links_processed?: Date | boolean;
    insert_timestamp?: string;
    update_timestamp?: string;
    source_search_result_id?: string;
    source_url?: string;
    link_count?: number;
    crawler_engine?: string;
    checking_failed?: string;
    processors_to_run?: string[];
    hop_count?: number;
    discovery_chain?: string[];
    root_search_result_id?: string;
}

// Interface for linked report sources
interface LinkedReportSource {
    _id: string;
    title?: string;
    filename?: string;
    source_url?: string;
    url?: string;
    insert_timestamp?: string;
}

const ReportSourceCandidateDetailView: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { token, authLoaded } = useAuth();
    const { t } = useTranslation();

    const [candidate, setCandidate] = useState<ReportSourceCandidate | null>(null);
    const [reportSources, setReportSources] = useState<LinkedReportSource[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const apiUrl = env.apiUrl;
    const apiKey = env.apiKey;

    // Fetch candidate details
    const fetchCandidate = useCallback(async () => {
        if (!id || !token) return;

        setLoading(true);
        setError(null);

        try {
            const response = await axios.get(
                `${apiUrl}/report-source-candidates/${id}`,
                {
                    headers: {
                        'x-api-key': apiKey,
                        'Authorization': `Bearer ${token}`
                    }
                }
            );
            setCandidate(response.data.candidate);
        } catch (err: any) {
            console.error('Error fetching candidate:', err);
            setError(err.response?.data?.error || 'Failed to load candidate');
        } finally {
            setLoading(false);
        }
    }, [id, token, apiUrl, apiKey]);

    // Fetch linked report sources
    const fetchReportSources = useCallback(async () => {
        if (!id || !token) return;

        try {
            const response = await axios.get(
                `${apiUrl}/report-source-candidates/${id}/report-sources`,
                {
                    headers: {
                        'x-api-key': apiKey,
                        'Authorization': `Bearer ${token}`
                    }
                }
            );
            setReportSources(response.data.reportSources || []);
        } catch (err: any) {
            console.error('Error fetching report sources:', err);
            // Don't show error for this - it's optional
        }
    }, [id, token, apiUrl, apiKey]);

    useEffect(() => {
        if (authLoaded && token && id) {
            fetchCandidate();
            fetchReportSources();
        }
    }, [authLoaded, token, id, fetchCandidate, fetchReportSources]);

    // Format date
    const formatDate = (dateString: string | undefined | null) => {
        if (!dateString) return '-';
        try {
            return new Date(dateString).toLocaleString();
        } catch {
            return '-';
        }
    };

    // Navigate to report source detail
    const navigateToReportSource = (reportSourceId: string) => {
        navigate(`/report-sources/${reportSourceId}`);
    };

    if (!authLoaded) {
        return <Loader text={t('loading.authentication')} />;
    }

    if (loading) {
        return (
            <div className="report-sources-container">
                <Loader text={t('reportSourceCandidates.detail.loading')} />
            </div>
        );
    }

    if (error || !candidate) {
        return (
            <div className="report-sources-container">
                <div className="error-container">
                    <i className="fas fa-exclamation-triangle"></i>
                    <h2>{t('reportSourceCandidates.detail.notFound')}</h2>
                    <p>{error || t('reportSourceCandidates.detail.loadError')}</p>
                    <button className="primary-button" onClick={() => navigate('/report-source-candidates')}>
                        <i className="fas fa-arrow-left"></i> {t('reportSourceCandidates.detail.backToList')}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="report-sources-container">
            {/* Header */}
            <div className="detail-header">
                <button className="back-button" onClick={() => navigate('/report-source-candidates')}>
                    <i className="fas fa-arrow-left"></i> {t('common.back')}
                </button>
                <h1>{t('reportSourceCandidates.detail.title')}</h1>
            </div>

            {/* URL Section */}
            <div className="detail-section">
                <h2 className="section-title">
                    <i className="fas fa-link"></i> {t('reportSourceCandidates.detail.urlInfo')}
                </h2>
                <div className="detail-card">
                    <div className="detail-row">
                        <label>{t('reportSourceCandidates.details.url')}:</label>
                        <a href={candidate.url} target="_blank" rel="noopener noreferrer" className="url-link">
                            {candidate.url}
                            <i className="fas fa-external-link-alt"></i>
                        </a>
                    </div>
                    <div className="detail-row">
                        <label>{t('reportSourceCandidates.details.type')}:</label>
                        {candidate.is_pdf ? (
                            <span className="badge badge-pdf">
                                <i className="fas fa-file-pdf"></i> PDF
                            </span>
                        ) : (
                            <span className="badge badge-html">
                                <i className="fas fa-globe"></i> HTML
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* Status Section */}
            <div className="detail-section">
                <h2 className="section-title">
                    <i className="fas fa-info-circle"></i> {t('reportSourceCandidates.detail.statusInfo')}
                </h2>
                <div className="detail-card">
                    <div className="detail-row">
                        <label>{t('reportSourceCandidates.headers.status')}:</label>
                        {candidate.checking_failed ? (
                            <span className="badge badge-error">
                                <i className="fas fa-times-circle"></i> {t('reportSourceCandidates.status.failed')}
                            </span>
                        ) : candidate.links_processed ? (
                            <span className="badge badge-success">
                                <i className="fas fa-check-circle"></i> {t('reportSourceCandidates.status.processed')}
                            </span>
                        ) : (
                            <span className="badge badge-pending">
                                <i className="fas fa-clock"></i> {t('reportSourceCandidates.status.pending')}
                            </span>
                        )}
                    </div>
                    {candidate.checking_failed && (
                        <div className="detail-row error">
                            <label>{t('reportSourceCandidates.details.error')}:</label>
                            <span className="error-text">{candidate.checking_failed}</span>
                        </div>
                    )}
                    <div className="detail-row">
                        <label>{t('reportSourceCandidates.details.crawlerEngine')}:</label>
                        <span>{candidate.crawler_engine || '-'}</span>
                    </div>
                    <div className="detail-row">
                        <label>{t('reportSourceCandidates.details.linkCount')}:</label>
                        <span>{candidate.link_count ?? '-'}</span>
                    </div>
                    <div className="detail-row">
                        <label>{t('reportSourceCandidates.details.linksProcessed')}:</label>
                        <span>
                            {candidate.links_processed === true ? t('common.yes') :
                             candidate.links_processed === false ? t('common.no') :
                             candidate.links_processed ? formatDate(String(candidate.links_processed)) : '-'}
                        </span>
                    </div>
                    {candidate.processors_to_run && candidate.processors_to_run.length > 0 && (
                        <div className="detail-row">
                            <label>{t('reportSourceCandidates.details.processors')}:</label>
                            <div className="processor-tags">
                                {candidate.processors_to_run.map((proc, idx) => (
                                    <span key={idx} className="processor-tag">{proc}</span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Discovery Path Section */}
            <div className="detail-section">
                <h2 className="section-title">
                    <i className="fas fa-route"></i> {t('reportSourceCandidates.detail.discoveryPath')}
                </h2>
                <div className="detail-card">
                    <div className="detail-row">
                        <label>{t('reportSourceCandidates.details.hopCount')}:</label>
                        <span>
                            {candidate.hop_count !== undefined ? (
                                <>
                                    <span className={`badge ${candidate.hop_count === 1 ? 'badge-success' : 'badge-info'}`}>
                                        {candidate.hop_count}
                                    </span>
                                    <span className="hop-description">
                                        {candidate.hop_count === 1
                                            ? ` (${t('reportSourceCandidates.details.directResult')})`
                                            : ` (${t('reportSourceCandidates.details.viaLinks', { count: candidate.hop_count - 1 })})`
                                        }
                                    </span>
                                </>
                            ) : '-'}
                        </span>
                    </div>
                    {candidate.source_url && (
                        <div className="detail-row">
                            <label>{t('reportSourceCandidates.details.sourceUrl')}:</label>
                            <a href={candidate.source_url} target="_blank" rel="noopener noreferrer" className="url-link">
                                {candidate.source_url}
                                <i className="fas fa-external-link-alt"></i>
                            </a>
                        </div>
                    )}
                    {candidate.root_search_result_id && (
                        <div className="detail-row">
                            <label>{t('reportSourceCandidates.details.rootSearchResultId')}:</label>
                            <span className="monospace">{candidate.root_search_result_id}</span>
                        </div>
                    )}
                    {candidate.source_search_result_id && (
                        <div className="detail-row">
                            <label>{t('reportSourceCandidates.details.searchResultId')}:</label>
                            <span className="monospace">{candidate.source_search_result_id}</span>
                        </div>
                    )}

                    {/* Discovery Chain - Full Path Visualization */}
                    {candidate.discovery_chain && candidate.discovery_chain.length > 0 && (
                        <div className="discovery-chain-section">
                            <label>{t('reportSourceCandidates.details.discoveryChain')}:</label>
                            <div className="chain-visualization">
                                {candidate.discovery_chain.map((chainUrl, index) => (
                                    <React.Fragment key={index}>
                                        <div className="chain-item-large">
                                            <div className="chain-step-container">
                                                <span className="chain-step-number">{index + 1}</span>
                                                <span className="chain-step-label">
                                                    {index === 0 ? t('reportSourceCandidates.detail.searchResult') :
                                                     index === candidate.discovery_chain!.length - 1 ? t('reportSourceCandidates.detail.currentPage') :
                                                     t('reportSourceCandidates.detail.hop')}
                                                </span>
                                            </div>
                                            <a href={chainUrl} target="_blank" rel="noopener noreferrer" className="chain-url-link">
                                                <span className="chain-url-text">{chainUrl}</span>
                                                <i className="fas fa-external-link-alt"></i>
                                            </a>
                                        </div>
                                        {index < candidate.discovery_chain!.length - 1 && (
                                            <div className="chain-connector">
                                                <i className="fas fa-arrow-down"></i>
                                            </div>
                                        )}
                                    </React.Fragment>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Linked Report Sources Section */}
            {reportSources.length > 0 && (
                <div className="detail-section">
                    <h2 className="section-title">
                        <i className="fas fa-file-alt"></i> {t('reportSourceCandidates.detail.linkedSources')}
                    </h2>
                    <div className="detail-card">
                        <p className="section-description">
                            {t('reportSourceCandidates.detail.linkedSourcesDescription')}
                        </p>
                        <div className="linked-sources-list">
                            {reportSources.map((source) => (
                                <div key={source._id} className="linked-source-item">
                                    <div className="source-info">
                                        <span className="source-title">
                                            {source.title || source.filename || t('common.unknown')}
                                        </span>
                                        {source.source_url && (
                                            <span className="source-url">{source.source_url}</span>
                                        )}
                                    </div>
                                    <button
                                        className="view-source-button"
                                        onClick={() => navigateToReportSource(source._id)}
                                    >
                                        <i className="fas fa-eye"></i> {t('reportSourceCandidates.detail.viewSource')}
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Timestamps Section */}
            <div className="detail-section">
                <h2 className="section-title">
                    <i className="fas fa-clock"></i> {t('reportSourceCandidates.detail.timestamps')}
                </h2>
                <div className="detail-card">
                    <div className="detail-row">
                        <label>{t('reportSourceCandidates.details.created')}:</label>
                        <span>{formatDate(candidate.insert_timestamp)}</span>
                    </div>
                    <div className="detail-row">
                        <label>{t('reportSourceCandidates.details.updated')}:</label>
                        <span>{formatDate(candidate.update_timestamp)}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReportSourceCandidateDetailView;
